import { useEffect } from "react";
import { LocaleLink } from "@/components/LocaleLink";
import { getSortedPosts, headingId, type BlogPost } from "@/lib/blog-posts";
import { getBlogTheme } from "@/lib/blog-themes";
import { trackArticleView } from "@/lib/analytics";
import { useScrollDepth } from "@/hooks/useScrollDepth";

type Props = { post: BlogPost; locale: "da" | "en" };

const LINK = /\[([^\]]+)\]\(([^)\s]+)\)/g;

/** Renders [text](/path) as internal links; everything else as plain text. */
function InlineText({ text }: { text: string }) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(LINK)) {
    parts.push(text.slice(last, m.index));
    parts.push(
      <LocaleLink
        key={m.index}
        to={m[2]}
        className="text-terracotta underline decoration-terracotta/40 underline-offset-2 hover:decoration-terracotta"
      >
        {m[1]}
      </LocaleLink>,
    );
    last = m.index! + m[0].length;
  }
  parts.push(text.slice(last));
  return <>{parts}</>;
}

function PostBody({ body }: { body: string }) {
  const paragraphs = body.split(/\n\n+/);
  return (
    <div className="space-y-6">
      {paragraphs.map((p, i) => {
        const trimmed = p.trim();
        const lines = trimmed.split("\n").map((l) => l.trim());
        // "![alt](src)" on its own is an inline image.
        const image = trimmed.match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/);
        if (image) {
          return (
            <figure key={i} className="mx-auto max-w-xl overflow-hidden rounded-sm bg-muted">
              <img
                src={image[2]}
                alt={image[1]}
                loading="lazy"
                decoding="async"
                className="aspect-[3/2] w-full object-cover"
              />
            </figure>
          );
        }
        // A block where every line starts with "- " is a bullet list.
        if (lines.every((l) => l.startsWith("- "))) {
          return (
            <ul key={i} className="list-disc space-y-1 pl-5 text-base leading-relaxed text-foreground/80">
              {lines.map((l, j) => (
                <li key={j}>
                  <InlineText text={l.slice(2)} />
                </li>
              ))}
            </ul>
          );
        }
        // A block where every line starts with "|" is a table: first row is the
        // header, and a "|---|" separator row is skipped.
        if (lines.every((l) => l.startsWith("|"))) {
          const rows = lines
            .filter((l) => !/^\|[\s:|-]+\|$/.test(l))
            .map((l) =>
              l
                .replace(/^\||\|$/g, "")
                .split("|")
                .map((cell) => cell.trim()),
            );
          const [head, ...bodyRows] = rows;
          return (
            <div key={i} className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-sm text-foreground/80 md:text-base">
                <thead>
                  <tr className="border-b border-border">
                    {head.map((cell, j) => (
                      <th key={j} className="py-2 pr-4 font-medium text-foreground">
                        {cell}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bodyRows.map((row, r) => (
                    <tr key={r} className="border-b border-border/60">
                      {row.map((cell, j) => (
                        <td key={j} className="py-2 pr-4">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        // "## " marks a subheading explicitly (needed for headings ending in
        // "?"). Older posts rely on the heuristic: short single-line paragraphs
        // that don't end in punctuation.
        const explicit = trimmed.startsWith("## ");
        const isHeading =
          explicit ||
          (!trimmed.includes("\n") && trimmed.length < 80 && !/[.?!»]$/.test(trimmed));
        if (isHeading) {
          const text = explicit ? trimmed.slice(3) : trimmed;
          return (
            <h2
              key={i}
              id={headingId(text)}
              className="scroll-mt-32 font-display text-2xl leading-tight text-foreground"
            >
              {text}
            </h2>
          );
        }
        return (
          <p key={i} className="text-base leading-relaxed text-foreground/80">
            <InlineText text={trimmed} />
          </p>
        );
      })}
    </div>
  );
}

export function BlogPostPage({ post, locale }: Props) {
  const c = locale === "da" ? post.da : post.en;
  useScrollDepth(`blog:${post.slug}`);
  useEffect(() => {
    trackArticleView({
      content_type: "blog",
      slug: post.slug,
      title: c.title,
      locale,
    });
  }, [post.slug, c.title, locale]);
  const theme = getBlogTheme(post.theme)!;
  const date = new Date(post.publishedAt).toLocaleDateString(
    locale === "da" ? "da-DK" : "en-GB",
    { year: "numeric", month: "long", day: "numeric" },
  );
  const minLabel = locale === "da" ? "min." : "min";
  const backLabel = `← ${theme[locale].title}`;
  const moreLabel = locale === "da" ? "Læs også" : "Read next";

  const others = getSortedPosts().filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <>
      <article className="mx-auto max-w-3xl px-6 pt-40 pb-16 md:pt-52 md:px-0">
        <LocaleLink
          to="/blog/$slug"
          params={{ slug: theme.id }}
          className="text-xs font-medium uppercase tracking-[0.22em] text-foreground/60 hover:text-terracotta"
        >
          {backLabel}
        </LocaleLink>

        <div className="mt-8 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          <LocaleLink
            to="/blog/$slug"
            params={{ slug: theme.id }}
            className="text-terracotta hover:underline"
          >
            {theme[locale].label}
          </LocaleLink>
          <span aria-hidden>·</span>
          <span>{date}</span>
          <span aria-hidden>·</span>
          <span>{post.readingMinutes} {minLabel}</span>
        </div>

        <h1 className="mt-4 font-display text-[clamp(2.4rem,5.5vw,4rem)] leading-[1.05] text-balance">
          {c.title}
        </h1>

        <p className="mt-6 text-lg leading-relaxed text-foreground/75">
          {c.excerpt}
        </p>

        <div className="mt-12 aspect-[5/3] overflow-hidden rounded-sm bg-muted">
          <img
            src={post.cover}
            alt=""
            loading="eager"
            decoding="async"
            className="h-full w-full object-cover"
          />
        </div>

        <div className="mt-12">
          <PostBody body={c.body} />
        </div>

        {post.gallery && post.gallery.length > 0 ? (
          <div className="mt-16 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {post.gallery.map((src, i) => (
              <div
                key={src}
                className={
                  // Editorial rhythm: occasional full-bleed image, rest in 2-col grid
                  i % 5 === 0
                    ? "sm:col-span-2 aspect-[5/3] overflow-hidden rounded-sm bg-muted"
                    : "aspect-[4/5] overflow-hidden rounded-sm bg-muted"
                }
              >
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              </div>
            ))}
          </div>
        ) : null}
      </article>

      <section className="mx-auto max-w-[1500px] border-t border-border/60 px-6 py-20 md:px-10 md:py-28">
        <h2 className="font-display text-2xl text-foreground">{moreLabel}</h2>
        <div className="mt-8 grid gap-10 md:grid-cols-3">
          {others.map((p) => {
            const oc = locale === "da" ? p.da : p.en;
            return (
              <LocaleLink
                key={p.slug}
                to="/blog/$slug/$post"
                params={{ slug: p.theme, post: p.slug }}
                className="group block"
              >
                <div className="aspect-[4/3] overflow-hidden rounded-sm bg-muted">
                  <img
                    src={p.cover}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  />
                </div>
                <h3 className="mt-4 font-display text-lg leading-tight text-foreground transition-colors group-hover:text-terracotta">
                  {oc.title}
                </h3>
              </LocaleLink>
            );
          })}
        </div>
      </section>
    </>
  );
}

